import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, useToggle } from '@factorialco/deprecated-design-system'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import IncidenceInformation from 'modules/bookkeepers/components/IncidenceInformation'
import ActionsButtons from 'modules/bookkeepers/components/IncidenceInformation/ActionsButtons'
import useFetchIncidenceDetails from 'modules/bookkeepers/hooks/useFetchIncidenceDetails'
import useGetCurrentIncidence from 'modules/bookkeepers/hooks/useGetCurrentIncidence'
import { CurrentIncidenceProvider } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { IncidenceDetailProvider } from 'modules/bookkeepers/lib/IncidenceDetailContext'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { translatedIncidenceName } from 'modules/bookkeepers/lib/dynamicTranslations'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import { INCIDENCE_STATUSES } from 'modules/payroll_bundle/lib/Incidences/Status'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import EditManualIncidenceModal from 'modules/payroll_bundle/pages/IncidencesPage/components/_ManualIncidenceModal/_Edit'

interface Props {
  incidencesTable: React.ReactNode
  incidenceDetail: React.ReactNode
  selectedIncidenceId: string | null | undefined
  loading: boolean
  onCloseSidepanel?: () => void
}

const IncidencesPageBody = ({
  incidencesTable,
  incidenceDetail,
  selectedIncidenceId,
  loading,
  onCloseSidepanel,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [openEditModal, toggleEditModal] = useToggle(false)
  const isBookkeeperView = useBookkeeperView()
  const { incidence: originalIncidence } = useGetCurrentIncidence()
  const [incidence, setIncidence] = React.useState(originalIncidence)
  const employeeName = incidence && incidence?.employee?.fullName
  const isManualIncidence = incidence?.name === INCIDENCE_TYPES.manual_incidence
  const isManualIncidenceEditable = !isBookkeeperView && isManualIncidence
  const showIncidenceInformation = !loading && incidenceDetail && selectedIncidenceId

  React.useEffect(() => {
    if (
      originalIncidence &&
      (originalIncidence.id !== incidence?.id || originalIncidence.status !== incidence?.status)
    ) {
      setIncidence(originalIncidence)
    }
  }, [originalIncidence, incidence?.id, incidence?.status])

  const updateIncidenceStatus = React.useCallback(
    (newStatus: string) => {
      if (incidence) {
        setIncidence({ ...incidence, status: newStatus })
      }
    },
    [incidence]
  )

  const { hasCompensations } = useHasCompensations()
  const incidencesRoute = hasCompensations
    ? navigation.compensations.employeeUpdates.fullPath()
    : navigation.payrollBundle.incidences.fullPath()

  const handleSidePanelClose = () => {
    if (onCloseSidepanel) {
      onCloseSidepanel()
    } else {
      navigate(incidencesRoute)
    }
  }
  const incidenceDetails = useFetchIncidenceDetails(incidence?.id)
  const { hasInhousePayroll } = useInhousePayroll()

  const showActionButtons = () => {
    if (hasInhousePayroll) {
      return true
    }
    return (
      incidence?.status === INCIDENCE_STATUSES.in_preparation ||
      incidence?.status === INCIDENCE_STATUSES.discarded
    )
  }

  return (
    <Box flexDirection="row" alignItems="flexStart" width="full">
      <Box paddingY="s16" width="full">
        {incidencesTable}
      </Box>
      {incidence && (
        <CurrentIncidenceProvider incidence={incidence}>
          <IncidenceDetailProvider incidenceDetails={incidenceDetails}>
            <Sidepanel
              open={!!showIncidenceInformation && !openEditModal}
              overlay={false}
              boxPadding={false}
              width="wide"
              title={
                employeeName
                  ? i18n.t('bookkeepers.incidence.details.header', {
                      incidence_name: translatedIncidenceName(incidence.name, incidence.customName),
                      employee_name: employeeName,
                    })
                  : (translatedIncidenceName(incidence.name, incidence.customName) ?? '')
              }
              onClose={handleSidePanelClose}
              headerBorder={false}
              closeLabel={i18n.t('common.close')}
              footer={
                showActionButtons() && (
                  <ActionsButtons updateIncidenceStatus={updateIncidenceStatus} />
                )
              }
            >
              <IncidenceInformation
                toggleEditModal={toggleEditModal}
                incidenceDetail={incidenceDetail}
              />
            </Sidepanel>
            {openEditModal && isManualIncidenceEditable && (
              <EditManualIncidenceModal
                manualIncidenceId={incidence.targetId}
                onClose={toggleEditModal(false)}
              />
            )}
          </IncidenceDetailProvider>
        </CurrentIncidenceProvider>
      )}
    </Box>
  )
}

export default IncidencesPageBody
